import { useEffect, useState } from "react"
import { sort } from "fast-sort"
import {
  Area,
  AreaChart,
  Bar,
  Brush,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import Plot from "react-plotly.js"
import "./styles.scss"
import moment from "moment"
import { CustomTooltipAreaESM } from "../Tooltip"
import { FaArrowDown, FaArrowUp, FaCircle, FaSquare } from "react-icons/fa"

export const formatHeatMapDataTable = (data, setData) => {
  let x1 = []
  let y1 = []
  let x2 = []
  let z = []

  try {
    y1.push(
      data.map((item, index) => {
        const codes = JSON.parse(item.codes) || []
        let fields = JSON.parse(item.fields)
        fields = {
          ["Químico"]: sort(fields.filter((f) => f.category === "Químico")).desc((f) => f.title),
          ["Biológico"]: sort(fields.filter((f) => f.category === "Biológico")).desc(
            (f) => f.title,
          ),
        }

        if (index === 0) {
          x1.push(
            ...fields["Químico"].map((row) => `${row.title} - ${row.category.toUpperCase()}`),
            ...fields["Biológico"].map((row) => `${row.title} - ${row.category.toUpperCase()}`),
          )

          x2.push(
            ...fields["Químico"].map((row) => row.category),
            ...fields["Biológico"].map((row) => row.category),
          )
        }

        z.push(
          [...fields["Químico"], ...fields["Biológico"]].map((field) =>
            Number(field.value || 0).toFixed(),
          ),
        )

        return `${index + 1}. ${item?.email}`
      }),
    )
  } catch (error) {
    console.log("Error", error)
  }

  const sumColumn = (colIndex) => {
    return z.reduce((acc, row) => acc + parseFloat(row[colIndex]), 0)
  }

  const numColumns = z[0].length
  const columnSums = Array.from({ length: numColumns }, (_, colIndex) => sumColumn(colIndex))
  const columnAverages = columnSums.map((sum) => (sum / z.length)?.toFixed(0))

  const mapUsersByCode = z
    .map((zs, index) => {
      return {
        user: y1[0][index],
        values: zs.map((item, i) => {
          return {
            [x1[i]]: item,
          }
        }),
      }
    })
    .filter((item) => item)

  const mapAverage = {
    user: "Média Geral",
    values: columnAverages.map((item, i) => {
      return {
        [x1[i]]: item,
      }
    }),
  }

  setData([mapAverage, ...mapUsersByCode])
}

export const formatHeatMapData = (data, setData) => {
  const values = {
    x1: [],
    x2: [
      "Químico",
      "Químico",
      "Químico",
      "Químico",
      "Químico",
      "Químico",
      "Químico",
      "Químico",
      "Químico",
      "Químico",
      "Biológico",
      "Biológico",
      "Biológico",
    ],
    y1: [],
    y2: [],
    z: [],
  }

  try {
    values.y1 = data.map((item, index) => {
      const codes = JSON.parse(item.codes) || []
      let fields = JSON.parse(item.fields)
      fields = {
        ["Químico"]: sort(fields.filter((f) => f.category === "Químico")).desc((f) => f.title),
        ["Biológico"]: sort(fields.filter((f) => f.category === "Biológico")).desc((f) => f.title),
      }

      if (index === 0) {
        values.x1 = [
          ...fields["Químico"].map((row) => `${row.title} - ${row.category.toUpperCase()}`),
          ...fields["Biológico"].map((row) => `${row.title} - ${row.category.toUpperCase()}`),
        ]

        values.x2 = [
          ...fields["Químico"].map((row) => row.category),
          ...fields["Biológico"].map((row) => row.category),
        ]
      }

      values.z.push(
        [...fields["Químico"], ...fields["Biológico"]].map((field) =>
          Number(field.value || 0).toFixed(),
        ),
      )

      return `${item.email} - ${codes.map((code) => code?.value).join(", ")}`
    })
  } catch (error) {
    console.log("Error", error)
  }

  setData(values)
}

export const HeatMapChart = ({ data }) => {
  const [chartData, setChartData] = useState({})
  const [colorscaleValue] = useState([
    [0, "#ff0101"],
    [0.2, "#ff8c02"],
    [0.4, "#ffcc03"],
    [0.6, "#42a67c"],
    [0.8, "#0b7348"],
    [1, "#0b7348"],
  ])

  const [layout, setLayout] = useState({
    height: "120%",
    width: 1600,
    annotations: [],
    coloraxis: {
      colorscale: colorscaleValue,
      showscale: true,
      cmin: 0,
      cmax: 100,
    },
    margin: { t: 50 },
    zaxis: { zmin: 0, zmax: 100, zauto: false },
    xaxis: {
      ticks: "",
      side: "bottom",
      constrain: "range",
      constraintoward: "center",
      anchor: "x1",
      automargin: "heigth",
    },
    xaxis2: {
      ticks: "",
      side: "top",
      overlaying: "x1",
      anchor: "x2",
      position: 0.9,
      categoryarray: [
        "Químico",
        "Químico",
        "Químico",
        "Químico",
        "Químico",
        "Químico",
        "Químico",
        "Químico",
        "Químico",
        "Químico",
        "Biológico",
        "Biológico",
        "Biológico",
      ],
    },
    yaxis: {
      ticks: "",
      ticksuffix: " ",
      anchor: "y1",
      autosize: true,
      scaleanchor: "x1",
      scaleratio: 1,
      range: [0, 10],
      automargin: "width",
    },
  })

  useEffect(() => {
    if (chartData.x1 || chartData.y1 || chartData.z) {
      let annotations = []
      for (let i = 0; i < chartData.y1.length; i++) {
        for (let j = 0; j < chartData.x1.length; j++) {
          let textColor = "#ffff"

          const result = {
            xref: "x1",
            yref: "y1",
            x: chartData.x1[j],
            y: chartData.y1[i],
            text: Number.isNaN(chartData.z[i][j]) ? 0 : chartData.z[i][j],
            font: {
              family: "Arial",
              size: 11,
              color: textColor, // Defina a cor com base no valor
            },
            showarrow: false,
          }
          annotations.push(result)
        }
      }

      setLayout({ ...layout, annotations })
    }
  }, [chartData])

  useEffect(() => {
    if (data && data.length) {
      formatHeatMapData(data, setChartData)
    }

    return () => setChartData({})
  }, [data])

  return (
    <div className="div-barometer-chart area">
      <div className="div-group heat_map">
        <Plot
          data={[
            {
              x: chartData.x1,
              y: chartData.y1,
              z: chartData.z,
              type: "heatmap",
              colorscale: colorscaleValue,
              showscale: true,
              connectgaps: false,
              zAxis: { domain: [0, 40] },
              hoverlabel: { align: "center" },
              xgap: 4,
              ygap: 4,
              xaxis: "x1",
            },
          ]}
          layout={layout}
          useResizeHandler={true}
          style={{
            width: "100%",
            height: "100%",
            padding: "0 32px",
            overflowX: "scroll",
          }}
          config={{
            locale: "pt-br",
            displaylogo: false,
          }}
        />
      </div>
    </div>
  )
}

export const CustomizedDot = ({ cx, cy }) => (
  <circle cx={cx - 10} cy={cy - 10} r={25} stroke="black" strokeWidth={3} fill="red" />
)

const colors = [
  { dataKey: "m (%)", color: "#264653" },
  { dataKey: "P (mg/dm3)", color: "#2A9D8F" },
  { dataKey: "pH", color: "#E9C46A" },
  { dataKey: "Cu (mg/dm3)", color: "#F4A261" },
  { dataKey: "Mn (mg/dm3)", color: "#E76F51" },
  { dataKey: "S (mg/dm3)", color: "#5F464B" },
  { dataKey: "Zn (mg/dm3)", color: "#8E4A49" },
  { dataKey: "V (%) ", color: "#7DAA92" },
  { dataKey: "B (mg/dm3)", color: "#80FFEC" },
  { dataKey: "MO (dag/kg ou %)", color: "#C2FBEF" },
  { dataKey: "IQS_tax", color: "#524632" },
  { dataKey: "IQS_Ec", color: "#8F7E4F" },
  { dataKey: "GF_total", color: "#C3C49E" },
]

const items = [
  {
    dataKey: "Geral",
    color: "#dedce2",
    childrens: [
      {
        dataKey: "Químico",
        color: "#00DDFF",
        childrens: [
          { dataKey: "m (%)", color: "#264653" },
          { dataKey: "P (mg/dm3)", color: "#2A9D8F" },
          { dataKey: "pH", color: "#E9C46A" },
          { dataKey: "Cu (mg/dm3)", color: "#F4A261" },
          { dataKey: "Mn (mg/dm3)", color: "#E76F51" },
          { dataKey: "S (mg/dm3)", color: "#5F464B" },
          { dataKey: "Zn (mg/dm3)", color: "#8E4A49" },
          { dataKey: "V (%) ", color: "#7DAA92" },
          { dataKey: "B (mg/dm3)", color: "#80FFEC" },
          { dataKey: "MO (dag/kg ou %)", color: "#C2FBEF" },
        ],
      },
      {
        dataKey: "Biológico",
        color: "#57FF00",
        childrens: [
          { dataKey: "IQS_tax", color: "#524632" },
          { dataKey: "IQS_Ec", color: "#8F7E4F" },
          { dataKey: "GF_total", color: "#C3C49E" },
        ],
      },
    ],
  },
]

export const CustomizedLegend = (props) => {
  const { payload, onClick, filter } = props

  console.log({ payload })

  const handleClick = (data, unique) => {
    onClick(data, unique)
  }

  const handleOpacity = (data) => {
    return !filter.find((item) => item === data) ? "0.3" : 1
  }

  const getItems = (items, level) => {
    if (!items) {
      return
    }

    return items.map((item) => {
      let entry = payload.find((a) => a.dataKey === item.dataKey)

      if (!entry) {
        entry = item
      }

      return (
        <>
          <li
            onDoubleClick={() => handleClick(entry.dataKey, true)}
            onClick={() => handleClick(entry.dataKey)}
            style={{
              cursor: "pointer",
              marginBottom: 3,
              display: "flex",
              alignItems: "center",
              gap: 5,
              marginLeft: level * 10,
              opacity: handleOpacity(entry.dataKey),
            }}>
            <div style={{ width: 10, height: 10, backgroundColor: `${entry.color}` }}></div>
            {entry.value || entry.dataKey}
          </li>
          {getItems(item.childrens, level + 1)}
        </>
      )
    })
  }

  return <ul>{getItems(items, 0)}</ul>
}

// Gerador de cores dinâmico
const generateColor = (index) => {
  const hue = (index * 137) % 360
  return `hsl(${hue}, 70%, 50%)`
}

// Função de regressão linear
const linearRegression = (data, key, monthsToPredict = 12) => {
  const validData = data.filter((d) => d[key] !== undefined)
  const n = validData.length
  const x = validData.map((_, index) => index + 1)
  const y = validData.map((d) => d[key])

  const sumX = x.reduce((acc, val) => acc + val, 0)
  const sumY = y.reduce((acc, val) => acc + val, 0)
  const sumXY = x.reduce((acc, val, i) => acc + val * y[i], 0)
  const sumX2 = x.reduce((acc, val) => acc + val * val, 0)

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n

  const lastIndex = n
  const predictions = []

  for (let i = 1; i <= monthsToPredict; i++) {
    let predictedValue = Math.max(0, slope * (lastIndex + i) + intercept)

    // Limitar a 100, exceto para "N.º de Produtores"
    if (key !== "N.º de Produtores") {
      predictedValue = Math.min(predictedValue, 100)
    }

    predictions.push(predictedValue)
  }

  return predictions
}

export const AreaHistoryLineChart = ({ data }) => {
  const [forecastData, setForecastData] = useState([])
  const [hiddenKeys, setHiddenKeys] = useState([])

  const allKeys = data.length > 0 ? Object.keys(data[0]).filter((key) => key !== "monthYear") : []

  const chartData = data.map((item) => ({
    ...item,
    year: item.monthYear,
  }))

  // Calcular melhoria para os cards
  const calculateImprovement = () => {
    if (chartData.length < 2) return {}

    const lastPeriod = forecastData.length
      ? forecastData[forecastData.length - 1]
      : chartData[chartData.length - 1]
    const improvement = {}

    allKeys.forEach((key) => {
      const firstValidPeriod = chartData.find((item) => item[key] > 0)
      const firstValue = firstValidPeriod ? firstValidPeriod[key] : 0
      const lastValue = lastPeriod[key] ?? 0

      const improvementPercentage =
        firstValue > 0 ? ((lastValue - firstValue) / firstValue) * 100 : lastValue > 0 ? 100 : 0

      improvement[key] = improvementPercentage.toFixed(2)
    })

    return improvement
  }

  const improvementData = calculateImprovement()

  // Botão de previsão para regressão linear
  const handleForecast = (monthsToPredict) => {
    const lastMonth = moment(chartData[chartData.length - 1].year, "MM/YYYY")
    const futureMonth = {
      monthYear: lastMonth.clone().add(monthsToPredict, "months").format("MM/YYYY"),
    }

    const predObj = { ...futureMonth }
    allKeys.forEach((key) => {
      const predictedValues = linearRegression(chartData, key, monthsToPredict)
      predObj[key] = predictedValues[monthsToPredict - 1] ?? 0
    })

    const mergedData = [...chartData]
    const exists = mergedData.find((d) => d.monthYear === predObj.monthYear)
    if (exists) {
      Object.assign(exists, predObj)
    } else {
      mergedData.push(predObj)
    }

    const sortedData = mergedData.sort(
      (a, b) => moment(a.year, "MM/YYYY") - moment(b.year, "MM/YYYY"),
    )
    setForecastData(sortedData)
  }

  // Esconder/Mostrar itens ao clicar na legenda
  const handleLegendClick = (e) => {
    const { dataKey } = e
    if (hiddenKeys.includes(dataKey)) {
      setHiddenKeys(hiddenKeys.filter((item) => item !== dataKey))
    } else {
      setHiddenKeys([...hiddenKeys, dataKey])
    }
  }

  // Renderizar cards com % de melhoria
  const renderImprovementCards = () => {
    return (
      <div className="d-flex flex-wrap justify-content-center my-4">
        {Object.keys(improvementData).map((key) => {
          const improvementValue = improvementData[key]
          const isPositive = improvementValue >= 0
          const cardColor = isPositive ? "#d4edda" : "#f8d7da"
          const textColor = isPositive ? "#155724" : "#721c24"

          if (parseFloat(improvementValue) === 0) return null

          return (
            <div
              className="card text-center m-2"
              style={{
                width: "18rem",
                borderRadius: "8px",
                border: `1px solid ${textColor}`,
                backgroundColor: cardColor,
                color: textColor,
              }}
              title={key}
              key={key}>
              <div className="card-body d-flex flex-column justify-content-center align-items-center">
                <div style={{display: "flex", gap: 5}}>
                  <div style={{ fontSize: "3rem", fontWeight: "bold", lineHeight: "1" }}>
                    {improvementValue}%
                  </div>
                  {isPositive ? (
                    <FaArrowUp size={20} style={{ color: "#28a745", marginTop: 8 }} />
                  ) : (
                    <FaArrowDown size={20} style={{ color: "#dc3545", marginTop: 8 }} />
                  )}
                </div>
                <p
                  className="card-title mt-2"
                  style={{
                    fontSize: "0.9rem",
                    maxWidth: "90%",
                    whiteSpace: "normal",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3, // Limita a três linhas
                    lineHeight: "1.4em",
                    marginBottom: 0
                  }}>
                  {key.replace(/^[^-]*-/, "")}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const generateColor = (index) => {
    const colors = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300"]
    return colors[index % colors.length]
  }

  return (
    <div className="div-barometer-chart area">
      {renderImprovementCards()}

      <div className="d-flex justify-content-center mb-3">
        <button className="btn btn-primary mx-2" onClick={() => handleForecast(12)}>
          Prever 1 ano
        </button>
        <button className="btn btn-primary mx-2" onClick={() => handleForecast(24)}>
          Prever 2 anos
        </button>
        <button className="btn btn-primary mx-2" onClick={() => handleForecast(60)}>
          Prever 5 anos
        </button>
      </div>

      <ResponsiveContainer width="100%" height={600}>
        <ComposedChart data={forecastData.length ? forecastData : chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="monthYear" />
          <YAxis yAxisId="left" />
          <Tooltip />
          <Legend onClick={handleLegendClick} />

          <Bar
            yAxisId="left"
            dataKey="N.º de Produtores"
            fill={generateColor(allKeys.indexOf("N.º de Produtores"))}
            barSize={40}
            hide={hiddenKeys.includes("N.º de Produtores")}
          />

          {allKeys
            .filter((key) => key !== "N.º de Produtores")
            .map((key, index) => (
              <Line
                key={key}
                yAxisId="left"
                type="monotone"
                dataKey={key}
                stroke={generateColor(index)}
                strokeWidth={2}
                hide={hiddenKeys.includes(key)}
              />
            ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

export const AreaLineChart = ({ size = "medium", data }) => {
  const [filter, setFilter] = useState(["Geral"])
  const resumeSize = Array.isArray(data) && data.length !== 0 ? data.length - 1 : 0

  const array = [
    {
      id: 0,
      label: "Sustentável",
      color: "rgba(11,115,72, 1)",
      value: 18,
      height: getSize(size, 35, 66, 186),
    },
    {
      id: 1,
      label: "Quase Sustentável",
      color: "rgba(66,166,124, 1)",
      value: 36,
      height: getSize(size, 71, 134, 372),
    },
    {
      id: 2,
      label: "Intermediário",
      color: "rgba(255,204,3, 1)",
      value: 54,
      height: getSize(size, 107, 202, 557),
    },
    {
      id: 3,
      label: "Quase Insustentável",
      color: "rgba(255, 140, 2, 1)",
      value: 72,
      height: getSize(size, 144, 270, 745),
    },
    {
      id: 4,
      label: "Insustentável",
      color: "rgba(255, 1, 1, 1)",
      value: 90,
      height: getSize(size, 179, 338, 929),
    },
  ]

  const fieldsToChart = []

  const chartData =
    Array.isArray(data) &&
    data.map((item, idx) => {
      const summary = JSON.parse(item.summary)
      const fields = JSON.parse(item.fields)
      let dataToReturn = {
        ...item,
        Químico: parseFloat(summary["Químico"]).toFixed(1),
        Biológico: parseFloat(summary["Biológico"]).toFixed(1),
        Geral: parseFloat(item.generalScore).toFixed(1),
        year:
          idx === 0
            ? 2021
            : parseFloat(summary["Químico"]).toFixed(1) == "5.0" &&
              parseFloat(summary["Biológico"]).toFixed(1) == "53.7" &&
              parseFloat(item.generalScore).toFixed(1) == "9.0"
            ? 2022
            : "",
      }

      fields.map((item) => {
        const color = colors.find((a) => a.dataKey === item.title)?.color
        dataToReturn = { ...dataToReturn, [item.title]: parseFloat(item.value).toFixed(1) }
        if (!fieldsToChart.find((a) => a.title === item.title)) {
          fieldsToChart.push({ title: item.title, value: parseFloat(item.value).toFixed(1), color })
        }
      })

      return dataToReturn
    })

  const handleFilter = (data, unique) => {
    if (unique) {
      setFilter([data])
    } else {
      if (!filter.find((item) => data.includes(item))) {
        setFilter(filter.concat(data))
      } else {
        setFilter(filter.filter((item) => !data.includes(item)))
      }
    }
  }

  return (
    <div className="div-barometer-chart area">
      <div className="div-group">
        <div className={`div-chart ${size}`}>
          {array.map((item, index) => {
            const style = {
              width: "100%",
              height: item.height,
              background: item.color,
              zIndex: array && array.length - index,
            }

            return <div className="bar" style={style}></div>
          })}
        </div>
        <ResponsiveContainer width={"100%"} height={400}>
          <ComposedChart
            data={chartData}
            style={{
              fontSize: "12px",
              zIndex: 9999,
            }}
            margin={{
              top: 0,
              right: 20,
              bottom: 0,
              left: 20,
            }}>
            <Legend
              layout="vertical"
              align="left"
              verticalAlign="top"
              content={<CustomizedLegend onClick={handleFilter} />}
              filter={filter}
            />
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis
              ticks={[0, 20, 40, 60, 80, 100]}
              type="number"
              dataKey="y"
              domain={[0, "dataMax"]}
            />
            <Tooltip content={<CustomTooltipAreaESM />} cursor={{ strokeDasharray: "3 3" }} />
            <Brush
              dataKey="year"
              height={30}
              stroke="#277f4a"
              startIndex={0}
              endIndex={resumeSize}
            />
            {filter.find((row) => row === "Geral") && (
              <Area type="linear" dataKey="Geral" stroke="#dedce2" fill="#dedce2" />
            )}
            {filter.find((row) => row === "Químico") && (
              <Line type="linear" dataKey="Químico" stroke="#00DDFF" strokeWidth={3} />
            )}
            {filter.find((row) => row === "Biológico") && (
              <Line type="linear" dataKey="Biológico" stroke="#57FF00" strokeWidth={3} />
            )}
            {fieldsToChart
              .map(
                (item) =>
                  filter.find((row) => row === item.title) && (
                    <Line type="linear" dataKey={item.title} stroke={item.color} strokeWidth={3} />
                  ),
              )
              .filter((item) => item)}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export const LineArea = ({ size = "medium" }) => {
  const mockData = [
    {
      name: "Page A",
      uv: 4000,
      pv: 2400,
      amt: 2400,
    },
    {
      name: "Page B",
      uv: 3000,
      pv: 1398,
      amt: 2210,
    },
    {
      name: "Page C",
      uv: 2000,
      pv: 9800,
      amt: 2290,
    },
    {
      name: "Page D",
      uv: 2780,
      pv: 3908,
      amt: 2000,
    },
    {
      name: "Page E",
      uv: 1890,
      pv: 4800,
      amt: 2181,
    },
    {
      name: "Page F",
      uv: 2390,
      pv: 3800,
      amt: 2500,
    },
    {
      name: "Page G",
      uv: 3490,
      pv: 4300,
      amt: 2100,
    },
  ]

  return (
    <ResponsiveContainer width={100} height={100}>
      <AreaChart
        width={200}
        height={60}
        data={mockData}
        margin={{
          top: 5,
          right: 0,
          left: 0,
          bottom: 5,
        }}>
        <Area type="monotone" dataKey="uv" stroke="#8884d8" fill="#8884d8" />
      </AreaChart>
    </ResponsiveContainer>
  )
}

export const getSize = (size, small, medium, large) => {
  return size === "small" ? small : size === "medium" ? medium : size === "large" ? large : 0
}
